import styled from "styled-components";
import clsx from "clsx";
import DOMPurify from "dompurify";
import {
  Connector,
  FieldName,
  Integration,
  Organization,
  ValidationError,
  ValidationErrors,
  ValidationSuccess,
} from "../../../config/types";
import { palette } from "../../../utils/theme";
import MergeLinkCardBase from "../../shared/MergeLinkCardBase";
import { useMemo } from "react";
import { snakeCaseToSpacedCapitalized } from "../../../services";
import CardHeader from "../../shared/CardHeader";
import { AlertTriangle } from "lucide-react";
import NextStepButton from "../../shared/NextStepButton";
import { Accordion, Text } from "@merge-api/merge-javascript-shared";
import ConnectorSetupStepFormHeader from "../connector-setup/ConnectorSetupStepFormHeader";
import useAgentHandlerLinkModalContext from "../../context/useAgentHandlerLinkModalContext";
import ValidationHeader from "./ValidationHeader";

const RemediationScreenContainer = styled.div`
  position: relative;
  overflow: auto;
  flex-grow: 1;
  flex-direction: column;
  transition: flex-grow 0.35s ease-out;
  align-items: center;
  mask-image: linear-gradient(to bottom, black calc(100% - 48px), transparent 100%);
  height: 1px;
`;

const ValidationErrorMessage = styled.div<{ $isFirst?: boolean; $isLast?: boolean }>`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 0.5px solid ${palette.darkBorder};
  ${({ $isFirst }) => (!$isFirst ? "border-top: none;" : "")}
  border-radius: ${({ $isFirst, $isLast }) =>
    `${$isFirst ? "8px 8px" : "0px 0px"} ${$isLast ? "8px 8px" : "0px 0px"}`};
`;

type Props = {
  customTitle?: String;
  customText?: React.ReactNode;
  validationErrors?: ValidationErrors;
  validationSuccesses?: ValidationSuccess[];
  integration?: Integration;
  organization?: Organization;
  connector?: Connector;
  customButtonText?: string;
  customSecondaryButtonText?: string;
  isPermissionsIssue?: boolean;
  isErrorRemediated?: boolean;
  isSuccessfulFlow?: boolean;
  isScraperStep?: boolean;
  alreadyValidatedFlow?: boolean;
  skipWithoutValidating?: () => void;
  onClickBottomAction: () => void;
  onClickBack?: () => void;
  onClickNext?: () => void;
  onExit: () => void;
};

const CommonModelFieldsListForFieldNames = (fieldNames: FieldName[]) => {
  return (
    <div className="flex flex-col gap-1 ml-2 pl-4 mt-2 border-l border-gray-10">
      {fieldNames?.map((fieldName: FieldName) => (
        <Text key={fieldName.field_name} variant="sm" className="text-gray-40">
          {snakeCaseToSpacedCapitalized(fieldName.field_name)}
        </Text>
      ))}
    </div>
  );
};

const ValidationErrorDiv = ({
  validationErrorKey,
  validationErrorGroup,
  integrationName,
  organizationName,
  isPermissionsIssue,
  translate,
  $isFirst,
  $isLast,
}: {
  validationErrorKey: string;
  validationErrorGroup: ValidationError[];
  integrationName: string | undefined;
  organizationName: string | undefined;
  isPermissionsIssue?: boolean;
  translate: (keyString: string, fallbackString: string) => string;
  $isFirst: boolean;
  $isLast: boolean;
}) => {
  const errorMessageFromValidationError = () => {
    // Assign by error code of first element group
    switch (validationErrorGroup[0].error_code) {
      case 400:
        return translate("bad-request", "Bad request");
      case 401:
        return translate("unauthorized", "Unauthorized");
      case 403:
        return translate("forbidden", "Forbidden");
      case 404:
        return translate("no-url", "URL not found");
      case 405:
        return translate("unrecognized-url", "Unrecognized URL");
      case 500:
        return translate("internal-error", "Internal server error");
      case 502:
        return translate("gateway-error", "Bad gateway");
      case 503:
        return translate("service-error", "Service unavailable");
      default:
        return translate("access-error", "Unable to access");
    }
  };

  const ORG_NAME_PLACEHOLDER = "{ORGANIZATION_NAME}";
  const INTEGRATION_NAME_PLACEHOLDER = "{INTEGRATION_NAME}";

  const replaceErrorDetailPlaceholders = (
    errorDetail: string,
    orgName: string | undefined,
    integrationName: string | undefined
  ) => {
    return errorDetail
      .replaceAll(ORG_NAME_PLACEHOLDER, orgName ?? "")
      .replaceAll(INTEGRATION_NAME_PLACEHOLDER, integrationName ?? "integration");
  };

  // we consolidate by detail so we can assume the first translated description is the same as the rest
  const firstID = validationErrorGroup[0].source_pointer;

  return (
    <ValidationErrorMessage $isFirst={$isFirst} $isLast={$isLast} key={validationErrorKey}>
      {isPermissionsIssue || validationErrorGroup.some((error) => error.readable_model_name) ? (
        <>
          {validationErrorGroup.map((validationError) => {
            <ValidationHeader title={validationError.readable_model_name ?? ""} isError />;
          })}
          <div className="flex flex-col gap-2">
            {validationErrorGroup.map((validationError) => {
              if (!validationError.readable_model_name) {
                return;
              }

              return (
                <Accordion
                  chevronOrientation="down"
                  chevronPlacement="right"
                  chevronSize={12}
                  title={<ValidationHeader title={validationError.readable_model_name ?? ""} isError />}
                  variant="none"
                  chevronColor="black"
                >
                  {CommonModelFieldsListForFieldNames(validationError?.field_names ?? [])}
                </Accordion>
              );
            })}
          </div>
        </>
      ) : (
        <ValidationHeader title={errorMessageFromValidationError()} isError />
      )}
      <Text variant="sm" className="text-gray-40 [&_a]:!text-blue-60 [&_a]:underline hover:[&_a]:!text-blue-80">
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              translate(
                `${firstID}_detail`,
                replaceErrorDetailPlaceholders(validationErrorKey, organizationName, integrationName)
              )
            ),
          }}
        />
      </Text>
    </ValidationErrorMessage>
  );
};

const ValidationSuccessesDiv = ({
  validationSuccesses,
}: {
  validationSuccesses: ValidationSuccess[];
}) => {
  return (
    <>
      {validationSuccesses.length > 0 && (
        <div className="flex flex-col max-h-full gap-2 border border-gray-10 rounded-lg p-3 mx-5 overflow-auto">
          {validationSuccesses.map((validationSuccess) => {
            if (!validationSuccess.readable_model_name) {
              return;
            }

            return (
              <Accordion
                chevronOrientation="down"
                chevronPlacement="right"
                chevronSize={12}
                title={<ValidationHeader title={validationSuccess.readable_model_name ?? ""} />}
                variant="none"
                chevronColor="black"
              >
                {CommonModelFieldsListForFieldNames(validationSuccess?.field_names ?? [])}
              </Accordion>
            );
          })}
        </div>
      )}
      <div className="flex flex-grow"></div>
    </>
  );
};

const ErrorScreen = ({
  customTitle,
  customText,
  validationErrors,
  validationSuccesses,
  integration,
  connector,
  organization,
  customButtonText,
  customSecondaryButtonText,
  isPermissionsIssue = false,
  isErrorRemediated = false,
  isSuccessfulFlow = false,
  isScraperStep,
  alreadyValidatedFlow = false,
  skipWithoutValidating,
  onClickBottomAction,
  onClickBack,
  onClickNext,
  onExit,
}: Props) => {
  const { translate } = useAgentHandlerLinkModalContext();
  const hasValidationErrors: boolean = (validationErrors?.length || 0) > 0;

  const hasValidationSuccesses: boolean = (validationSuccesses?.length || 0) > 0;

  const validationErrorMap: { [errorDetail: string]: ValidationError[] } = useMemo(() => {
    const tmp = Object.fromEntries([]);
    for (const validationError of validationErrors ?? []) {
      if (!Object.keys(tmp).includes(validationError.detail)) {
        tmp[validationError.detail] = [];
      }
      tmp[validationError.detail].push(validationError);
    }
    return tmp;
  }, [validationErrors]);

  const BottomAction = (
    <div className="w-full px-5 pb-5">
      {!alreadyValidatedFlow &&
      ((hasValidationErrors && isPermissionsIssue) || (isSuccessfulFlow && onClickNext)) ? (
        <div className="flex flex-col">
          {/* Go Back */}
          <NextStepButton
            onClick={() => {
              // there is a bang here but if isSuccessfulFlow is true onClickNext will exist as per the check above
              isSuccessfulFlow ? onClickNext!() : onClickBottomAction();
            }}
          >
            {customButtonText ?? isSuccessfulFlow
              ? translate("next-button", "Next")
              : translate("retry-access", "Retry access")}
          </NextStepButton>
          {/* Continue */}
          {skipWithoutValidating && !isSuccessfulFlow && (
            <NextStepButton
              onClick={() => {
                skipWithoutValidating();
              }}
              inverted
              className="mt-3"
            >
              {customSecondaryButtonText ?? translate("skip-for-later", "Skip for later")}
            </NextStepButton>
          )}
        </div>
      ) : (
        <>
          <NextStepButton
            onClick={() => (isErrorRemediated ? onClickBack!() : onClickBottomAction())}
            inverted={!customButtonText}
          >
            {customButtonText ?? translate("go-back-button", "Go back")}
          </NextStepButton>
        </>
      )}
    </div>
  );

  return (
    <MergeLinkCardBase>
      <CardHeader onBack={onClickBack} onExit={() => onExit!()} />

      {isSuccessfulFlow ? (
        <>
          <ConnectorSetupStepFormHeader
            connector={connector as Connector}
            header={
              hasValidationSuccesses
                ? customTitle ??
                  translate("org-data-access-title", `${organization?.name} can access this data`)
                : null
            }
          />
          {validationSuccesses && (
            <ValidationSuccessesDiv validationSuccesses={validationSuccesses} />
          )}
        </>
      ) : hasValidationErrors ? (
        <>
          <ConnectorSetupStepFormHeader connector={connector as Connector} />
          <RemediationScreenContainer>
            {validationSuccesses && validationSuccesses?.length > 0 && (
              <>
                <Text variant="h4" className="mx-5 pb-3">
                  {customTitle ??
                    translate(
                      "org-data-access-title",
                      `${organization?.name} can access this data`
                    )}
                </Text>
                <ValidationSuccessesDiv validationSuccesses={validationSuccesses} />
              </>
            )}
            <div className={clsx("mx-5 mb-6", hasValidationSuccesses ? "mt-6" : "")}>
              <div className="pb-3 flex flex-col gap-y-0.5">
                {isScraperStep || validationSuccesses?.length == 0 ? (
                  <Text variant="h4">{translate("fix-issues-header", "Fix the issues below")}</Text>
                ) : (
                  <>
                    <Text variant="h6">
                      {translate("data-below-inaccessible", "The data below remains inaccessible")}
                    </Text>
                    <Text as="p" variant="sm" className="text-gray-40">
                      {translate(
                        "help-center-starting-sentence",
                        "If you need this data, troubleshoot below."
                      )}
                    </Text>
                  </>
                )}
              </div>

              <div className="flex flex-col w-full">
                {Object.entries(validationErrorMap).map(
                  ([validationErrorKey, validationErrorGroup], ind, arr) => (
                    <ValidationErrorDiv
                      validationErrorKey={validationErrorKey}
                      validationErrorGroup={validationErrorGroup}
                      integrationName={integration?.name}
                      organizationName={organization?.name}
                      isPermissionsIssue={isPermissionsIssue}
                      translate={translate}
                      $isFirst={ind === 0}
                      $isLast={ind === arr.length - 1}
                      key={`${ind}-${validationErrorKey}`}
                    />
                  )
                )}
              </div>
            </div>
          </RemediationScreenContainer>
        </>
      ) : isErrorRemediated ? (
        <div className="flex flex-grow flex-col items-center justify-center">
          <ConnectorSetupStepFormHeader
            connector={connector as Connector}
            header={translate("fix-issues-header", "Fix the issues below")}
          />
          <RemediationScreenContainer>
            <div className="flex flex-col w-full px-5 pb-5">
              <ValidationErrorMessage $isFirst $isLast>
                <ValidationHeader
                  title={
                    customText
                      ? translate("error", "Error")
                      : translate("invalid-credentials", "Invalid credentials")
                  }
                  isError
                />
                <Text variant="md" as="p" className="text-gray-40">
                  {customText ??
                    translate(
                      "default-invalid-credentials-text",
                      "One or more of the inputs you've provided are invalid – please check your inputs and try again"
                    )}
                </Text>
              </ValidationErrorMessage>
            </div>
          </RemediationScreenContainer>
        </div>
      ) : (
        <div className="flex flex-col size-full justify-center items-center">
          <AlertTriangle size={64} />
          <Text variant="sm" className="mt-6 px-6 text-center">
            {customText ??
              translate(
                "default-invalid-credentials-text",
                "One or more of the inputs you've provided are invalid – please check your inputs and try again"
              )}
          </Text>
        </div>
      )}
      {BottomAction}
    </MergeLinkCardBase>
  );
};

export default ErrorScreen;
